import { AngledBand } from '@carouselgg/nexus/angled-band'
import { DisplayHeading } from '@carouselgg/nexus/display-heading'
import { GradientText } from '@carouselgg/nexus/gradient-text'
import { ScrollReveal } from '@carouselgg/nexus/scroll-reveal'
import { StatHighlight } from '@carouselgg/nexus/stat-highlight'
import type { GradientPreset } from '@carouselgg/nexus/gradient-text'
import { Search, ClipboardList, ShieldAlert } from '@carouselgg/nexus/icon'
import { useTrackVisibility } from '../hooks/useTrackVisibility'
import styles from './Problems.module.css'

const problems: {
  seed: string
  icon: typeof Search
  stat: string
  statSuffix: string
  hook: string
  body: string
  gradient: GradientPreset
}[] = [
  {
    seed: '01',
    icon: Search,
    stat: 'Word of',
    statSuffix: 'mouth',
    hook: "That's how you find a competition.",
    body: "There's no real way to find a competition or a team. Everything is buried in Discord servers and Reddit threads. If you don't already know somebody, you don't play — and that's most people.",
    gradient: 'brand',
  },
  {
    seed: '02',
    icon: ClipboardList,
    stat: '0',
    statSuffix: 'real tools.',
    hook: 'Leagues run on duct tape.',
    body: 'Excel for scheduling. Discord bots for standings. League organizers are grinding their hearts out with tools that were never built for this — and they burn out because the ops never scale.',
    gradient: 'sunset',
  },
  {
    seed: '03',
    icon: ShieldAlert,
    stat: '1 player.',
    statSuffix: '5 accounts.',
    hook: 'Smurfing kills leagues.',
    body: 'Without verified identity, skilled players spin up alt accounts to sandbag into lower brackets — “smurfing.” They stomp, everyone else quits, and the league dies. It is the single biggest killer of amateur esports.',
    gradient: 'ocean',
  },
]

export function Problems() {
  const ref = useTrackVisibility<HTMLDivElement>('problems')

  return (
    <AngledBand id="problem" tone="elevated" slice="top">
      <div className={styles.inner} ref={ref}>
        <ScrollReveal>
          <p className={styles.eyebrow}>
            <span className={styles.seed}>02</span>
            <span className={styles.rule} />
            If you have tried to play, you know
          </p>
        </ScrollReveal>

        <div className={styles.blocks}>
          {problems.map((problem, i) => (
            <ScrollReveal
              key={problem.stat}
              delay={i * 80}
              className={styles.block}
              direction={i % 2 === 0 ? 'left' : 'right'}
            >
              <div className={styles.statSide}>
                <span className={styles.blockSeed} aria-hidden="true">{problem.seed}</span>
                <StatHighlight
                  icon={<problem.icon size={28} />}
                  value={problem.stat}
                  suffix={problem.statSuffix}
                  tagline={problem.hook}
                  gradient={problem.gradient}
                />
              </div>
              <p className={styles.body}>{problem.body}</p>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal>
          <div className={styles.bridge}>
            <span className={styles.elbow} aria-hidden="true" />
            <DisplayHeading size="5xl">
              <GradientText gradient="brand" angle={115}>We are fixing all of it.</GradientText>
            </DisplayHeading>
            <span className={styles.elbow} aria-hidden="true" />
          </div>
        </ScrollReveal>
      </div>
    </AngledBand>
  )
}
